import {IconButton} from '@wandb/components';
import React, {useCallback} from 'react';

import {useProjectParams} from '../../pages/ProjectPage/useProjectParams';
import {useRampFlagFullScreenLinePlotEditing} from '../../util/rampFeatureFlags/rampFeatureFlags';
import {panelViews} from '../PanelBank/types';
import {useGetIsMediaPanelSettingsDrawerSupportedForPanel} from '../PanelMediaBrowser/useGetIsMediaPanelSettingsDrawerSupportedForPanel';
import {
  type DrawerContentType,
  useWorkspaceDrawerContext,
} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {FullScreenPanelLink} from './FullScreenPanel/FullScreenPanelLink';
import {useIsPanelEditButtonVisible} from './hooks/useIsPanelEditButtonVisible';
import {StyledButtonWrapper} from './PanelActions.style';
import {usePanelContext} from './PanelContextProvider';
import {PanelPopupMode} from './types';

/**
 * This is soon to be deprecated.
 */
export const PanelEditButton = () => {
  const {panel} = usePanelContext();

  // Media panels have their own flows
  if (panel.viewType === panelViews.mediaBrowser) {
    return <PanelBankMediaPanelSettingsButton />;
  }

  if (panel.viewType === panelViews.marimo) {
    return <PanelBankSettingsDrawerButton contentType="marimo-settings" />;
  }

  if (panel.viewType === panelViews.evaluationTables) {
    return <EvaluationTablesPanelSettingsButton buttonSize="sm" />;
  }

  // For the rest of panel types, we use the panel editor modal since
  // we haven not devoted time to migrating them into a drawer
  return <EditPanelWithModalButton buttonSize="sm" />;
};

export const EditPanelWithModalButton = ({
  buttonSize,
}: {
  buttonSize: 'sm' | 'md';
}) => {
  const {setPanelPopupMode, trackPanelControls} = usePanelContext();

  const onClick = useCallback(() => {
    setPanelPopupMode(PanelPopupMode.EDIT);
    trackPanelControls({action: 'Edit panel clicked'});
  }, [setPanelPopupMode, trackPanelControls]);

  return <EditButton buttonSize={buttonSize} onClick={onClick} />;
};

const EditButton = ({
  buttonSize,
  onClick,
}: {
  buttonSize: 'sm' | 'md';
  onClick: () => void;
}) => {
  return (
    <StyledButtonWrapper data-test="edit-panel-button">
      <IconButton
        icon="settings"
        variant="ghost"
        aria-label="Edit panel"
        data-dd-action-name="Edit panel"
        size={buttonSize}
        tooltip={{children: 'Edit panel'}}
        onPress={e => {
          e.continuePropagation();
          onClick();
        }}
      />
    </StyledButtonWrapper>
  );
};

export const FullScreenPanelEditButton = () => {
  const {entityName} = useProjectParams();
  const {panel, panelBankSectionConfigRef, panelRef} = usePanelContext();
  const isPanelEditButtonVisible = useIsPanelEditButtonVisible(
    entityName,
    panel.viewType
  );
  const isFullScreenLinePlotEditingEnabled =
    useRampFlagFullScreenLinePlotEditing();
  const {openWorkspaceDrawer} = useWorkspaceDrawerContext();

  if (!isPanelEditButtonVisible) {
    return null;
  }

  // Media panels have their own flows
  if (panel.viewType === panelViews.mediaBrowser) {
    return <FullScreenMediaPanelSettingsButton />;
  }

  if (panel.viewType === panelViews.marimo) {
    return (
      <EditButton
        buttonSize="md"
        onClick={() => {
          openWorkspaceDrawer?.(
            'marimo-settings',
            panelBankSectionConfigRef,
            panelRef
          );
        }}
      />
    );
  }

  if (panel.viewType === panelViews.evaluationTables) {
    return (
      <EditButton
        buttonSize="md"
        onClick={() => {
          openWorkspaceDrawer?.(
            'evaluation-tables-settings',
            panelBankSectionConfigRef,
            panelRef
          );
        }}
      />
    );
  }

  // For runs line plot panels, open in full screen with drawer open
  if (
    panel.viewType === 'Run History Line Plot' &&
    isFullScreenLinePlotEditingEnabled
  ) {
    return (
      <EditButton
        buttonSize="md"
        onClick={() => {
          openWorkspaceDrawer?.(
            'line-plot-editor',
            panelBankSectionConfigRef,
            panelRef
          );
        }}
      />
    );
  }

  // For the rest of panel types, we use the panel editor modal since
  // we haven not devoted time to migrating them into a drawer
  return <EditPanelWithModalButton buttonSize="md" />;
};

const FullScreenMediaPanelSettingsButton = () => {
  const {panel, panelRef, panelBankSectionConfigRef, trackPanelControls} =
    usePanelContext();
  const {openWorkspaceDrawer} = useWorkspaceDrawerContext();

  const getIsMediaPanelSettingsDrawerSupportedForPanel =
    useGetIsMediaPanelSettingsDrawerSupportedForPanel(panel);

  const onClick = useCallback(() => {
    window.scrollTo(0, 0);

    openWorkspaceDrawer?.(
      'media-settings',
      panelBankSectionConfigRef,
      panelRef
    );
    trackPanelControls({action: 'Edit panel clicked'});
  }, [
    openWorkspaceDrawer,
    panelBankSectionConfigRef,
    panelRef,
    trackPanelControls,
  ]);

  if (getIsMediaPanelSettingsDrawerSupportedForPanel()) {
    // We're already in full screen mode, so we can just open the drawer
    // Notice that the button is a different size in full screen mode
    return <EditButton buttonSize="md" onClick={onClick} />;
  }

  return <EditPanelWithModalButton buttonSize="md" />;
};

const PanelBankSettingsDrawerButton = ({
  contentType,
}: {
  contentType: DrawerContentType;
}) => {
  const {panel, panelRef, panelBankSectionConfigRef, trackPanelControls} =
    usePanelContext();
  const {openWorkspaceDrawer} = useWorkspaceDrawerContext();

  const onClick = useCallback(() => {
    window.scrollTo(0, 0);

    openWorkspaceDrawer?.(contentType, panelBankSectionConfigRef, panelRef);
    trackPanelControls({action: 'Edit panel clicked'});
  }, [
    openWorkspaceDrawer,
    panelBankSectionConfigRef,
    panelRef,
    trackPanelControls,
    contentType,
  ]);

  return (
    <FullScreenPanelLink
      panel={panel}
      panelBankSectionConfigRef={panelBankSectionConfigRef}>
      <EditButton buttonSize="sm" onClick={onClick} />
    </FullScreenPanelLink>
  );
};

const EvaluationTablesPanelSettingsButton = ({
  buttonSize,
}: {
  buttonSize: 'sm' | 'md';
}) => {
  const {panelRef, panelBankSectionConfigRef, trackPanelControls} =
    usePanelContext();
  const {openWorkspaceDrawer} = useWorkspaceDrawerContext();

  const onClick = useCallback(() => {
    openWorkspaceDrawer?.(
      'evaluation-tables-settings',
      panelBankSectionConfigRef,
      panelRef
    );
    trackPanelControls({action: 'Edit panel clicked'});
  }, [
    openWorkspaceDrawer,
    panelBankSectionConfigRef,
    panelRef,
    trackPanelControls,
  ]);

  return <EditButton buttonSize={buttonSize} onClick={onClick} />;
};

const PanelBankMediaPanelSettingsButton = () => {
  const {panel} = usePanelContext();

  const getIsMediaPanelSettingsDrawerSupportedForPanel =
    useGetIsMediaPanelSettingsDrawerSupportedForPanel(panel);

  // When the media panel type is supported and we're just on a workspace, we want to
  // 1. Go into full screen panel page
  // 2. Open the media settings drawer by default
  if (getIsMediaPanelSettingsDrawerSupportedForPanel()) {
    return <PanelBankSettingsDrawerButton contentType="media-settings" />;
  }
  return <EditPanelWithModalButton buttonSize="sm" />;
};
